// src/config/colors.jsx
// Colors extracted from Figma design

export const COLORS = {
  // Primary colors
  primary: {
    purple: '#4A2EDF',
    green: '#32E170',
    lightBlue: '#52C5FF',
    brightGreen: '#39FF14',
    purpleLight: '#8E79FF',
    purpleLighter: '#F6F4FF', // For focus effects
    purpleAccent: '#735BF3', // For input focus/border
    blue: '#3B82F6', // Blue for buttons - Updated to match Figma design
    blueDark: '#2563EB', // Darker blue for hover states
    red: '#EF4444', // Red for error states and recording indicator
    lightPurple: '#D4C4FC', // Light purple accent from Figma
  },
  
  // Text colors
  text: {
    primary: '#E5E7EB', // Updated to match Figma design - Light text for dark theme
    secondary: '#D1D5DB', // Updated to match Figma design - Medium light text
    tertiary: '#9CA3AF', // Updated to match Figma design - Lighter text for less emphasis
    dark: '#101828', // Dark text color from Figma (for chat bubbles)
    gray: '#667085', // Gray text color from Figma (for subtitles, card values)
    darkGray: '#465366', // Darker gray from Figma (for section titles, icons)
    placeholder: '#969DAD', // Placeholder text color from Figma
  },
  
  // Background colors
  background: {
    primary: '#111827', // Updated to match Figma design - Dark background for main content
    secondary: '#1F2937', // Updated to match Figma design - Slightly lighter dark background
    tertiary: '#374151', // Updated to match Figma design - Even lighter dark background
    chatBubble: {
      ai: '#1F2937', // Updated to match Figma design - AI chat bubble background
      user: '#3B82F6', // Updated to match Figma design - User chat bubble background
    }
  },
  
  // Border colors
  border: {
    light: '#4B5563', // Updated to match Figma design - Light border for dark theme
    medium: '#374151', // Updated to match Figma design - Medium border for dark theme
    dark: '#1F2937', // Updated to match Figma design - Dark border for dark theme
  },
  
  // Icon colors
  icon: {
    primary: '#E5E7EB', // Updated to match Figma design - Primary icon color for dark theme
    secondary: '#9CA3AF', // Updated to match Figma design - Secondary icon color for dark theme
    accent: '#3B82F6', // Updated to match Figma design - Accent icon color (blue)
  },
  
  // Gradients
  gradients: {
    // AI Icon gradients
    aiIconBackground: `
      #1F2937,
      linear-gradient(135deg, rgba(31, 41, 55, 0) 0%, rgba(31, 41, 55, 1) 100%),
      radial-gradient(circle at 100% 65%, rgba(59, 130, 246, 0.2) 0%, rgba(59, 130, 246, 0) 100%),
      radial-gradient(circle at -5% -11%, rgba(50, 225, 112, 0.3) 0%, rgba(50, 225, 112, 0) 100%)
    `,
    aiIconBorder: `
      radial-gradient(circle at 2% 59%, rgba(55, 65, 81, 1) 0%, rgba(55, 65, 81, 0) 100%),
      linear-gradient(135deg, rgba(59, 130, 246, 0) 0%, rgba(59, 130, 246, 0.4) 100%),
      linear-gradient(135deg, rgba(50, 225, 112, 0) 0%, rgba(50, 225, 112, 1) 100%)
    `,
    
    // Right sidebar gradients
    rightSidebarBackground: `
      linear-gradient(135deg, rgba(31, 41, 55, 0) 0%, rgba(31, 41, 55, 1) 100%),
      radial-gradient(circle at 100% 63%, rgba(59, 130, 246, 0.2) 0%, rgba(59, 130, 246, 0) 100%),
      radial-gradient(circle at 50% 0%, rgba(82, 197, 255, 0.2) 0%, rgba(82, 197, 255, 0) 100%),
      radial-gradient(circle at 2% 100%, rgba(50, 225, 112, 0.2) 0%, rgba(50, 225, 112, 0) 100%)
    `,
    rightSidebarBorder: `
      radial-gradient(circle at 2% 59%, rgba(55, 65, 81, 1) 0%, rgba(55, 65, 81, 0) 100%),
      linear-gradient(135deg, rgba(59, 130, 246, 0) 0%, rgba(59, 130, 246, 1) 100%),
      linear-gradient(135deg, rgba(50, 225, 112, 0) 0%, rgba(50, 225, 112, 1) 100%),
      linear-gradient(135deg, rgba(82, 197, 255, 1) 0%, rgba(82, 197, 255, 0) 100%)
    `,
  }
};

// UI Constants
export const UI = {
  borderRadius: {
    small: '4px',
    medium: '8px', // Updated to match Figma design
    large: '12px', // Updated to match Figma design
    xl: '20px',
    circle: '50%',
    chatInput: '0px', // Updated to match Figma design - No border radius for chat input
  },
  
  fontFamily: {
    primary: 'Roboto, sans-serif', // Updated to match Figma design
    icons: 'Material Icons Round',
  },
  
  fontSize: {
    xs: '0.75rem', // 12px
    small: '0.875rem', // 14px
    medium: '1rem', // 16px
    large: '1.125rem', // 18px
    xl: '1.25rem', // 20px
    xxl: '1.5rem', // 24px
  },
  
  lineHeight: {
    xs: '1.1em', // For very small text
    small: '1.2em', // For small text
    medium: '1.375em', // 22px for 16px font (137.5%) - From Figma
    large: '1.4em', // For larger text
    xl: '1.5em', // For headings
  },
  
  fontWeight: {
    regular: 400, // Regular weight from Figma
    medium: 500, // Medium weight
    semibold: 600, // Semibold weight
    bold: 700, // Bold weight
  },
  
  spacing: {
    xs: '4px',
    small: '8px',
    medium: '12px',
    large: '16px',
    xl: '24px',
    xxl: '32px',
  },
  
  shadows: {
    chatInput: 'none', // Updated to match Figma design - No shadow for chat input
    card: '0px 4px 12px rgba(0, 0, 0, 0.2)', // Added for card shadows
    dropdown: '0px 4px 12px rgba(0, 0, 0, 0.2)', // Added for dropdown shadows
  },
  
  // Text styles from Figma
  textStyles: {
    lead: {
      color: '#101828', // From Figma var(--Layout-V2-Title-Text)
      fontFamily: 'Roboto',
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '22px', // 137.5%
    }
  }
};

// Component-specific colors from Figma
export const COMPONENT_COLORS = {
  // Header font colors
  header: {
    title: '#4A2EDF', // Primary gradient color from title "Intake Agent"
    subtitle: '#667085', // "2.0 Flash" text color (fill_95OB1S)
    icons: '#465366', // Header icons color (fill_Z40QJ1)
  },
  
  // Main content font colors
  mainContent: {
    primary: '#101828', // Main text color in chat bubbles (fill_BIIRME)
    secondary: '#465366', // Secondary text color (fill_Z40QJ1)
    placeholder: '#969DAD', // Placeholder text in input field (fill_AD0645)
    buttonText: '#101828', // Button text color (fill_BIIRME)
  },
  
  // Right sidebar font colors
  sidebar: {
    title: '#101828', // "Requirements" title (fill_BIIRME)
    sectionTitle: '#465366', // Section titles (Job Details, Role, etc.) (fill_Z40QJ1)
    cardTitle: '#101828', // Card titles (fill_BIIRME)
    cardValue: '#667085', // Card values (fill_95OB1S)
    emptyText: '#969DAD', // Empty placeholder text (fill_AD0645)
    gradientText: {
      primary: '#8E79FF', // Primary gradient color
      secondary: '#52C5FF', // Secondary gradient color
      accent: '#39FF14', // Accent gradient color
    }
  },
  
  // Footer font colors
  footer: {
    primary: '#101828', // Primary text (fill_BIIRME)
    secondary: '#465366', // Secondary text and icons (fill_Z40QJ1)
  }
};
